import { ApolloError, DocumentNode, QueryHookOptions, gql } from '@apollo/client';
import { TimeDuration } from 'modules/campaigns/alerts/types';
import { useMemo } from 'react';
import { useGraphql } from 'utils/hooks/useGraphql';

type QueryCampaignsHasRecentTriggeredAlertsVariables = {
  campaignIds: number[];
  alertTimeWindow: TimeDuration;
};

type QueryCampaignsHasRecentTriggeredAlertsOptions = QueryHookOptions<
  any,
  QueryCampaignsHasRecentTriggeredAlertsVariables
>;

export type UseCampaignsHasRecentTriggeredAlertsState = {
  errorMessage?: ApolloError;
  hasError: boolean;
  isLoading: boolean;
  refetch: (variables: Partial<QueryCampaignsHasRecentTriggeredAlertsVariables>) => void;
  campaignTriggeredAlerts: {
    hasRecentAlerts: boolean;
    campaignId: number;
  }[];
};

export const queryCampaignsHasRecentTriggeredAlerts = gql`
  query queryCampaignsHasRecentTriggeredAlerts($campaignIds: [Long!]!, $alertTimeWindow: String!) {
    campaignTriggeredAlerts(campaignIds: $campaignIds, alertTimeWindow: $alertTimeWindow) {
      hasRecentAlerts
      campaignId
    }
  }
`;

export const useCampaignsHasRecentTriggeredAlerts = (
  options: QueryCampaignsHasRecentTriggeredAlertsOptions,
  query: DocumentNode = queryCampaignsHasRecentTriggeredAlerts,
): UseCampaignsHasRecentTriggeredAlertsState => {
  const queryOptions: QueryCampaignsHasRecentTriggeredAlertsOptions = useMemo(
    () => ({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      skip: !options?.variables?.campaignIds?.length || !options?.variables?.alertTimeWindow,
      ...options,
    }),
    [options],
  );

  const {
    data,
    errorMessage,
    isLoading,
    error: hasError = false,
    refetch,
  } = useGraphql(query, queryOptions);

  return {
    campaignTriggeredAlerts: data?.campaignTriggeredAlerts ?? [],
    errorMessage,
    hasError,
    isLoading,
    refetch,
  };
};
